import { useMemo, useState } from 'react';
import { compareByType, type ColumnValueType, type SortDirection } from './comparators.js';

export interface SortColumn<T> {
  type: ColumnValueType;
  /** Raw value to compare — not the formatted display string. E.g. `priceCents`, not `formatCents(priceCents)`. */
  value(row: T): unknown;
}

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export type AriaSort = 'ascending' | 'descending' | 'none';

export interface UseTableSortResult<T> {
  /** `rows`, sorted per the current state. Identity-stable (same array) when nothing is sorted. */
  rows: T[];
  sortState: SortState;
  /**
   * Cycles a column through ascending → descending → back to the table's
   * original order — a third click clears the sort entirely rather than
   * forcing the admin to fight their way back to "how it was".
   */
  toggleSort(columnId: string): void;
  ariaSortFor(columnId: string): AriaSort;
}

/**
 * Single-column sort, client-side, for the small datasets these admin tables
 * hold. Each column supplies its own value type (`string` | `number` |
 * `date` | `boolean`); the comparator for that type lives in
 * `comparators.ts` so every table gets the same ordering rules — a name
 * column always case-insensitive/locale-aware, a money or quantity column
 * always numeric, a date column always by timestamp, never by accident a
 * lexicographic string compare on a formatted value.
 */
export function useTableSort<T>(rows: T[], columns: Record<string, SortColumn<T>>): UseTableSortResult<T> {
  const [sortState, setSortState] = useState<SortState>({ column: null, direction: 'asc' });

  function toggleSort(columnId: string) {
    if (!columns[columnId]) return;
    setSortState((prev) => {
      if (prev.column !== columnId) return { column: columnId, direction: 'asc' };
      if (prev.direction === 'asc') return { column: columnId, direction: 'desc' };
      return { column: null, direction: 'asc' };
    });
  }

  const sorted = useMemo(() => {
    if (!sortState.column) return rows;
    const column = columns[sortState.column];
    if (!column) return rows;
    // A copy, never an in-place `rows.sort()` — the page's own state (or a
    // prop straight from the API response) must never be mutated by a
    // header click.
    return [...rows].sort((a, b) =>
      compareByType(column.type, column.value(a), column.value(b), sortState.direction),
    );
  }, [rows, columns, sortState]);

  function ariaSortFor(columnId: string): AriaSort {
    if (sortState.column !== columnId) return 'none';
    return sortState.direction === 'asc' ? 'ascending' : 'descending';
  }

  return { rows: sorted, sortState, toggleSort, ariaSortFor };
}
